import { Func2 } from "redux";
import { emptyReducer } from "./index";
import { buildPropertyGetter, keyFromArrayByStartsWith } from "../objectUtils";

const difference = require("lodash/difference");

export type TExtendReducerFunctionResult = [string[], string[]];

export type TExtendFunction = (
    action: any,
    keys: string[],
    ...args
) => TExtendReducerFunctionResult;

export function buildTypeExtendableReducer(
    getTypedReducer: Func2<string, any, any>,
    extendFunction: TExtendFunction,
    staticReducers = {},
    prefillStateBy = undefined
) {
    const staticKeys = Object.keys(staticReducers);

    return <S>(state = prefillStateBy as S, action, ...args) => {
        const activeState = state || ({} as S);
        const allKeys = Object.keys(activeState);

        const [dynamicKeys, keysToRemove] = extendFunction(
            action,
            difference(allKeys, staticKeys),
            ...args
        );

        const newObj = {} as S;
        let shouldUpdate = keysToRemove?.length > 0;

        for (let key of staticKeys) {
            newObj[key] = staticReducers[key](
                activeState[key],
                action,
                ...args
            );
            shouldUpdate = shouldUpdate || newObj[key] !== activeState[key];
        }

        const uniqueDynamicKeys =
            dynamicKeys?.length > 0 ? new Set(dynamicKeys) : [];
        for (let key of uniqueDynamicKeys) {
            const subState = activeState[key] ?? null;
            const reducer = getTypedReducer(key, subState);

            newObj[key] = reducer(subState, action, key, ...args);
            shouldUpdate = shouldUpdate || newObj[key] !== activeState[key];
        }

        for (let key of keysToRemove) {
            delete newObj[key];
        }

        return shouldUpdate ? newObj : state;
    };
}

export const buildKeyTypedReducerGetter = reducersObject =>
    buildPropertyGetter(reducersObject, emptyReducer, keyFromArrayByStartsWith);

export const buildTypeExtendableReducerCombineFn = (
    reducerTypes,
    extendFunction: TExtendFunction
) => staticReducers =>
    buildTypeExtendableReducer(
        buildKeyTypedReducerGetter(reducerTypes),
        extendFunction,
        staticReducers
    );

export const buildKeyTypedExtendableReducer = ({
    extendFunction = null as TExtendFunction,
    reducerTypes,
    staticReducers = {},
    prefillStateBy = undefined,
}) =>
    buildTypeExtendableReducer(
        buildKeyTypedReducerGetter(reducerTypes),
        extendFunction,
        staticReducers,
        prefillStateBy
    );
